import { type ReactNode, useCallback } from "react";
import { MoreVerticalIcon } from "vega-icons";

import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { ThemedButton } from "../Buttons/ThemedButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../shadcn/dropdown-menu";

import { ActionMenuItem, type ActionMenuItemProps } from "./ActionMenuItem";

export type MenuItem = ActionMenuItemProps;

export type ActionMenuProps = {
  menuItems?: MenuItem[];
  isLoading?: boolean;
  className?: string;
  disabled?: boolean;
  withBorders?: boolean;
  dataTestId?: string;
  compact?: boolean;
  variant?: "primary" | "secondary" | "tertiary";
  fullWidth?: boolean;
  renderTrigger?: (props: { isOpen: boolean; toggle: () => void }) => ReactNode;
  contentClassName?: string;
  align?: "center" | "end" | "start";
};

export const ActionMenu = ({
  renderTrigger,
  menuItems,
  isLoading = false,
  disabled = false,
  className,
  withBorders = true,
  dataTestId,
  compact = true,
  variant = "secondary",
  fullWidth = false,
  contentClassName,
  align = "end",
}: ActionMenuProps) => {
  const { isOpen, setIsOpen, toggle } = useBoolean(false);
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (disabled) return;
      setIsOpen(open);
    },
    [disabled, setIsOpen],
  );

  // Don't render if no menu items are provided
  if (!menuItems?.length) return null;

  return (
    <div className={cnTwMerge({ "skeleton-box": isLoading }, className)}>
      <DropdownMenu onOpenChange={handleOpenChange} open={isOpen}>
        {renderTrigger ? (
          <DropdownMenuTrigger asChild>
            {renderTrigger({
              isOpen,
              toggle,
            })}
          </DropdownMenuTrigger>
        ) : (
          <DropdownMenuTrigger asChild>
            <ThemedButton
              ariaLabel="Actions"
              className={cnTwMerge(
                isOpen && "bg-icon-button/no-background/pressed",
                compact && !fullWidth && "h-10 w-10 ml-0",
                !withBorders && "border-none",
              )}
              dataTestId={
                dataTestId
                  ? `${dataTestId}-action-menu-button`
                  : "action-menu-button"
              }
              disabled={disabled}
              fullWidth={fullWidth}
              icon={<MoreVerticalIcon />}
              iconPosition="right"
              onClick={toggle}
              variant={variant}
            >
              {!compact && m.btn_moreOptions()}
            </ThemedButton>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent
          align={align}
          className={cnTwMerge("p-2", contentClassName)}
        >
          {menuItems.map(({ className, disabled, icon, title, onClick }) => (
            <ActionMenuItem
              className={className}
              dataTestId={
                dataTestId
                  ? `${dataTestId}-action-menu-item-${title}`
                  : `action-menu-item-${title}`
              }
              disabled={disabled}
              icon={icon}
              key={title}
              onClick={onClick}
              title={title}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
